import { useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PATHS } from '../../routes/paths';
import DSLoading from '../../components/designSystem/DSLoading';
import { authRequest } from '../../core/api/authApiClient';
import { useAppDispatch } from '../../store/hooks';
import { setSession } from '../../Module/Auth/Presentation/authSlice';

type SsoExchangeResponse = {
  result_Code?: number;
  result_message?: string;
  token?: {
    accessToken?: string;
  };
  user?: {
    user_id?: string;
    email?: string;
    [key: string]: unknown;
  };
  project_id?: string;
  movepath?: string;
};

const PROJECT_ID_REQUIRED_PATHS = new Set([
  PATHS.POST_MANAGEMENT,
  PATHS.DONOR_MANAGEMENT,
  PATHS.PAYMENT_STATUS,
  PATHS.PROJECT_SETTINGS,
]);

const resolveMovePath = (rawPath?: string) => {
  if (!rawPath) return PATHS.PROJECT_MANAGEMENT;
  const path = rawPath.trim();
  if (!path.startsWith('/')) return PATHS.PROJECT_MANAGEMENT;
  return path;
};

const SsoCallback = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation('translation');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code')?.trim() ?? '';

      if (!code) {
        window.alert(t('page.sso.errors.missingCode'));
        navigate(PATHS.LOGIN, { replace: true });
        return;
      }

      try {
        const response = await authRequest<SsoExchangeResponse>('/sso/exchange', {
          body: { code },
          withAccessToken: false,
        });

        if (cancelled) return;

        const resultCode = Number(response?.result_Code ?? -1);
        const accessToken = String(response?.token?.accessToken ?? '').trim();
        const userId = String(response?.user?.user_id ?? '').trim();

        if (resultCode !== 0 || !accessToken || !userId) {
          throw new Error(String(response?.result_message ?? 'SSO exchange failed'));
        }

        const email = String(response?.user?.email ?? '').trim();

        dispatch(
          setSession({
            accessToken,
            issuedAt: new Date().toISOString(),
            user: {
              ...(response.user ?? {}),
              id: userId,
              user_id: userId,
              email,
              name: email || userId,
            },
          }),
        );

        const movePath = resolveMovePath(response?.movepath);
        const projectId = String(response?.project_id ?? '').trim();
        const navigateOptions =
          projectId && PROJECT_ID_REQUIRED_PATHS.has(movePath)
            ? { replace: true, state: { projectId } }
            : { replace: true };

        navigate(movePath, navigateOptions);
      } catch (error) {
        if (cancelled) return;
        console.error('SSO exchange failed:', error);
        window.alert(t('page.sso.errors.exchangeFailed'));
        navigate(PATHS.LOGIN, { replace: true });
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [dispatch, navigate, t]);

  return (
    <Container>
      <DSLoading variant="centered" />
    </Container>
  );
};

const Container = styled.div`
  min-height: 100dvh;
  width: 100%;
  display: flex;
`;

export default SsoCallback;
